import type { Property } from '~/core/entities/property';
import type { PaginatedPropertyFilters } from '~/core/entities/property/filters';
import type { MaybeRefOrGetter } from 'vue';

import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { slugify } from '~~/utils';

interface AgentPortfolio {
	agentSlug: string;
	properties: Property[];
	total: number;
}

export function useAgentPortfolio(agentSlug: MaybeRefOrGetter<string>) {
	const { user } = useUserSession();
	const getCookieData = useCookieData();
	const logger = useLogger('agentPortfolio');
	const slug = computed(() => toValue(agentSlug));
	const filters = computed<
		Pick<
			PaginatedPropertyFilters,
			'agentSlug' | 'isExclusive' | 'transactionType'
		>
	>(() => ({
		agentSlug: slug.value,
		isExclusive: false,
		transactionType: 'sell'
	}));
	const key = computed(() => `agent:${slug.value}:portfolio`);

	const emptyPortfolio = (currentSlug: string): AgentPortfolio => ({
		agentSlug: currentSlug,
		properties: [],
		total: 0
	});

	const { data, status } = useAsyncData(
		key,
		async (): Promise<AgentPortfolio> => {
			const currentSlug = slug.value;

			if (!currentSlug) return emptyPortfolio(currentSlug);

			try {
				const [result, error] = await nuxtPropertiesRepository.list(
					{
						...filters.value,
						page: 1,
						perPage: 12,
						markUserWishlistsId: user.value?.id
					},
					getCookieData()
				);

				if (error) {
					logger.warn('Failed to load agent portfolio', {
						agent_slug: currentSlug,
						error
					});
					return emptyPortfolio(currentSlug);
				}

				if (!result) {
					logger.warn('Agent portfolio request returned no result', {
						agent_slug: currentSlug
					});
					return emptyPortfolio(currentSlug);
				}

				return {
					agentSlug: currentSlug,
					properties: result.properties ?? [],
					total: result.pagination?.filteredCount ?? 0
				};
			} catch (error) {
				logger.warn('Failed to load agent portfolio', {
					agent_slug: currentSlug,
					error
				});
				return emptyPortfolio(currentSlug);
			}
		},
		{
			default: () => emptyPortfolio(slug.value),
			// O HTML do perfil usa cache compartilhado; favoritos são pessoais.
			server: false,
			lazy: true
		}
	);

	// A troca de rota pode ocorrer antes de o Nuxt atualizar os dados da nova chave.
	const currentPortfolio = computed(() =>
		data.value?.agentSlug === slug.value ? data.value : null
	);
	const properties = computed(() => currentPortfolio.value?.properties ?? []);

	const seeAllFilters = computed(() => ({
		...filters.value,
		locationSlug: getMostFrequentLocationSlug(properties.value)
	}));

	return {
		properties,
		total: computed(() => currentPortfolio.value?.total ?? 0),
		isLoading: computed(
			() =>
				Boolean(slug.value) &&
				(status.value === 'idle' || status.value === 'pending')
		),
		filters,
		seeAllFilters
	};
}

function getMostFrequentLocationSlug(
	properties: Property[]
): string | undefined {
	const counts = new Map<string, number>();

	for (const { city, state } of properties) {
		if (!city || !state) continue;

		const locationSlug = [city, state, 'brasil'].map(slugify).join('-');
		counts.set(locationSlug, (counts.get(locationSlug) ?? 0) + 1);
	}

	let mostFrequent: string | undefined;
	let highestCount = 0;

	for (const [locationSlug, count] of counts) {
		if (count > highestCount) {
			mostFrequent = locationSlug;
			highestCount = count;
		}
	}

	return mostFrequent;
}
